import { motion } from 'framer-motion';
import { Heart, ImageOff } from 'lucide-react';
import type { ContentItem } from '../../types';

interface ContentGridCardProps {
  item: ContentItem;
  onTap: () => void;
  onAction?: () => void;
  actionIcon?: React.ReactNode;
  renderMeta?: (item: ContentItem) => React.ReactNode;
  renderTags?: (item: ContentItem) => React.ReactNode;
}

export function ContentGridCard({
  item,
  onTap,
  onAction,
  actionIcon,
  renderMeta,
  renderTags,
}: ContentGridCardProps) {
  return (
    <motion.div
      className="bg-background-card rounded-xl shadow-sm overflow-hidden cursor-pointer border border-border"
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      whileHover={{ y: -2, boxShadow: '0 8px 20px rgba(0,0,0,0.08)' }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.2 }}
      onClick={onTap}
    >
      {/* Image */}
      <div
        className="h-48 bg-background-surface flex items-center justify-center relative"
        style={
          item.image_url
            ? { backgroundImage: `url(${item.image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }
            : {}
        }
      >
        {!item.image_url && (
          <ImageOff className="w-12 h-12 text-text-muted" />
        )}

        {/* Quick action button */}
        {onAction && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={(e) => {
              e.stopPropagation();
              onAction();
            }}
            className="absolute top-2 right-2 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-sm hover:bg-success hover:text-white transition-colors group"
          >
            {actionIcon || <Heart className="w-4 h-4 text-text-secondary group-hover:text-white" />}
          </motion.button>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-heading text-sm font-semibold text-text-primary mb-1 line-clamp-2">
          {item.title}
        </h3>

        {item.description && (
          <p className="text-xs text-text-secondary mb-2 line-clamp-2">
            {item.description}
          </p>
        )}

        {/* Custom meta info */}
        {renderMeta && (
          <div className="flex items-center gap-2 text-xs text-text-secondary mb-2">
            {renderMeta(item)}
          </div>
        )}

        {/* Tags */}
        {renderTags ? (
          renderTags(item)
        ) : item.tags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {item.tags.slice(0, 3).map((tag) => (
              <span
                key={tag.id}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary"
              >
                {tag.name}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </motion.div>
  );
}
